export type GoogleDocType = 'document' | 'presentation' | 'folder';

export interface ExternalDocument {
  id: string;
  jobId: string;
  invitationId: string | null;
  provider: string;
  docType: GoogleDocType;
  fileId: string | null;
  webViewLink: string;
  title: string | null;
  syncMode: string;
  slideCount: number | null;
  embedUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleWorkspaceStatus {
  configured: boolean;
  connected: boolean;
  email: string | null;
  connectedAt: string | null;
  scopes: string[];
  templates?: GoogleWorkspaceTemplates;
}

export interface GoogleWorkspaceTemplates {
  briefDocUrl?: string;
  storyboardSlidesUrl?: string;
  campaignFolderUrl?: string;
}

export interface BulkLinkResult {
  results: Array<{ jobId: string; linked: string[]; error?: string }>;
}

const DOC_HINT = 'https://docs.google.com/document/d/...';
const SLIDES_HINT = 'https://docs.google.com/presentation/d/...';
const FOLDER_HINT = 'https://drive.google.com/drive/folders/...';

export function googleUrlPlaceholder(docType: GoogleDocType): string {
  if (docType === 'document') return DOC_HINT;
  if (docType === 'presentation') return SLIDES_HINT;
  return FOLDER_HINT;
}

export function docTypeLabel(docType: GoogleDocType): string {
  if (docType === 'document') return 'Google Doc (brief)';
  if (docType === 'presentation') return 'Google Slides (storyboard)';
  return 'Google Drive folder';
}

export interface BriefImportProposal {
  title?: string;
  description?: string;
  objectives?: string;
  targetAudience?: string;
  keyMessages?: string;
  contentTypes?: string[];
  hashtags?: string[];
  complianceRequirements?: string;
  deliverablesChecklist?: Array<{ label: string }>;
  exampleScript?: string;
  estimatedKOL?: number;
  totalBudgetRM?: number;
  demo?: boolean;
}

export interface BriefImportPreview {
  sourceText: string;
  source: 'api' | 'export' | 'demo';
  current: BriefImportProposal;
  proposed: BriefImportProposal;
  documentId: string;
  documentTitle: string | null;
}

export interface StoryboardImportPreview {
  source: 'api' | 'demo';
  slideCount: number;
  sourceText: string;
  proposedScript: string;
  documentId: string;
  scriptId?: string;
  kolName?: string;
  demo?: boolean;
}

export interface BriefExportResult {
  webViewLink: string;
  fileId: string;
  demo?: boolean;
}

export interface DocumentSyncStatus {
  documentId: string;
  docType: string;
  title: string | null;
  syncMode: string;
  remoteModifiedAt: string | null;
  lastSyncedAt: string | null;
  lastContentHash: string | null;
  currentContentHash: string | null;
  hasUpdates: boolean;
  watchEnabled: boolean;
  demo?: boolean;
}

export interface GoogleComment {
  id: string;
  author: string;
  content: string;
  createdAt: string;
  resolved: boolean;
  anchorText?: string;
  alreadyImported?: boolean;
}

export interface SlideThumbnail {
  index: number;
  pageId: string;
  title: string;
  thumbnailUrl: string | null;
  textPreview: string;
}

export const BRIEF_IMPORT_FIELDS: Array<{
  key: keyof BriefImportProposal;
  label: string;
}> = [
  { key: 'title', label: 'Title' },
  { key: 'description', label: 'Description' },
  { key: 'objectives', label: 'Objectives' },
  { key: 'targetAudience', label: 'Target audience' },
  { key: 'keyMessages', label: 'Key messages' },
  { key: 'contentTypes', label: 'Content types' },
  { key: 'hashtags', label: 'Hashtags' },
  { key: 'complianceRequirements', label: 'Compliance' },
  { key: 'deliverablesChecklist', label: 'Deliverables' },
  { key: 'exampleScript', label: 'Example script' },
  { key: 'estimatedKOL', label: 'Estimated KOLs' },
  { key: 'totalBudgetRM', label: 'Total budget (RM)' },
];

export function formatBriefFieldValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (typeof value[0] === 'object' && value[0] && 'label' in value[0]) {
      return (value as Array<{ label: string }>).map((d) => d.label).join(', ');
    }
    return (value as string[]).join(', ');
  }
  return String(value);
}

export function briefFieldChanged(
  current: BriefImportProposal,
  proposed: BriefImportProposal,
  key: keyof BriefImportProposal
): boolean {
  return formatBriefFieldValue(current[key]) !== formatBriefFieldValue(proposed[key]);
}
